import { useCallback, useMemo, useState } from 'react';

import { EMPTY_MESSAGES } from '@/shared/lib/constants';
import { BadgeList } from '@/shared/ui/BadgeList';

import type { TagItem } from '@/entities/tags';

import { useGlossaryTree, useUpdateAssetGlossaryTerms } from '@/features/asset-detail/model/useGlossaryTerms';

type UseAssetTermSelectParams = {
  entityType: string;
  entityId: string;
  fqn: string;
  glossaryTerms: TagItem[];
  glossaryTermsDisplay: TagItem[];
  onTermClick: (termFqn: string) => void;
};

export const useAssetTermSelect = ({
  entityType,
  entityId,
  fqn,
  glossaryTerms,
  glossaryTermsDisplay,
  onTermClick,
}: UseAssetTermSelectParams) => {
  const { mutateAsync: updateTerms, isPending: isUpdatingTerms } = useUpdateAssetGlossaryTerms();
  const { data: glossaryTree = [] } = useGlossaryTree();

  const existingTermFQNs = useMemo(() => new Set(glossaryTerms.map((t) => t.tagFQN)), [glossaryTerms]);

  const glossaryTermsMap = useMemo(() => {
    const termMap = new Map<string, { description: string; displayName: string }>();
    glossaryTree.forEach((node) => {
      if (node.children) {
        node.children.forEach((child) => {
          termMap.set(child.key, {
            description: child.description ?? '',
            displayName: child.title,
          });
        });
      }
    });
    return termMap;
  }, [glossaryTree]);

  const handleSaveTerms = useCallback(
    async (newTermFQNs: string[]) => {
      await updateTerms({
        entityType,
        entityId,
        fqn,
        newTermFQNs,
        glossaryTermsMap,
      });
    },
    [updateTerms, entityType, entityId, fqn, glossaryTermsMap]
  );

  const renderTermBadgeList = useCallback(() => {
    return (
      <BadgeList
        items={glossaryTermsDisplay}
        onItemClick={onTermClick}
        iconName="book"
        existingValues={existingTermFQNs}
        emptyText={EMPTY_MESSAGES.NO_CONNECTED_TERMS}
      />
    );
  }, [glossaryTermsDisplay, onTermClick, existingTermFQNs]);

  const [isOpenEditTermModal, setIsOpenEditTermModal] = useState(false);

  const termTreeSelectValue = useMemo(() => {
    if (!glossaryTermsDisplay) return [];
    return glossaryTermsDisplay.map((term) => ({ value: term.tagFQN, label: term.displayName ?? term.name }));
  }, [glossaryTermsDisplay]);

  return {
    glossaryTree,
    termTreeSelectValue,
    isUpdatingTerms,
    isOpenEditTermModal,
    renderTermBadgeList,
    handleSaveTerms,
    setIsOpenEditTermModal,
  };
};
